const features = [
  {
    icon: "ri-delete-bin-line",
    title: "Smart Junk Removal",
    desc: "Automatically identifies and removes cached files, temporary data, and redundant system leftovers that clog your storage.",
  },
  {
    icon: "ri-image-2-line",
    title: "Duplicate Photo Finder",
    desc: "Scans your entire photo library and surfaces exact duplicates and near-identical shots, helping you recover precious storage.",
  },
  {
    icon: "ri-cpu-line",
    title: "Memory Booster",
    desc: "Intelligently clears inactive memory so foreground apps have maximum resources and run noticeably smoother.",
  },
  {
    icon: "ri-battery-charge-line",
    title: "Battery Health Insights",
    desc: "Monitor your battery\u2019s condition and get actionable tips to extend its longevity and maintain peak charge performance.",
  },
  {
    icon: "ri-hard-drive-2-line",
    title: "Storage Analyzer",
    desc: "Visualize exactly what is taking up space on your device with a clear, category-by-category breakdown.",
  },
  {
    icon: "ri-refresh-line",
    title: "Auto-Clean Scheduler",
    desc: "Set it and forget it. Scheduled cleaning runs silently in the background so your iPhone stays optimized around the clock.",
  },
];

const beforeAfter = [
  { label: "Storage Freed", value: "Up to 4 GB" },
  { label: "Performance Gain", value: "+60%" },
  { label: "Junk Files Cleared", value: "10,000+" },
  { label: "Time Required", value: "Under 1 min" },
];

export default function CleanerSection() {
  return (
    <section id="cleaner" className="py-24 md:py-36 bg-[#f8f8f6] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 md:mb-28">
          {/* Image */}
          <div className="relative flex justify-center order-2 lg:order-1">
            <div className="relative w-64 md:w-80 rounded-[40px] overflow-hidden border border-gray-100">
              <img
                src="https://public.readdy.ai/ai/img_res/a42c67697c7607fa961c91ab085398a0.jpg"
                alt="MAHHERC Cleaner app interface"
                className="w-full h-auto object-cover object-top"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute top-8 -right-4 bg-[#0f0f0f] rounded-2xl px-4 py-3">
              <div className="text-xs text-white/50 mb-1">Freed Today</div>
              <div className="text-sm font-bold text-cyan-400 whitespace-nowrap">1.2 GB Cleaned</div>
            </div>
            <div className="absolute bottom-12 -left-4 bg-white border border-gray-100 rounded-2xl px-4 py-3">
              <div className="text-xs text-gray-400 mb-1">Status</div>
              <div className="text-sm font-bold text-gray-900 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 inline-block" />
                Optimized
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="order-1 lg:order-2">
            <span className="text-xs uppercase tracking-[0.3em] text-gray-400 font-medium mb-4 block">
              / Product 02 /
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight mb-6">
              MAHHERC Cleaner
              <br />
              <span className="text-gray-300">Your iPhone, at Its Best</span>
            </h2>
            <p className="text-gray-500 text-base md:text-lg leading-relaxed mb-8">
              Your iPhone accumulates digital clutter over time — junk files,
              duplicate photos, fragmented cache. MAHHERC Cleaner finds it
              all and removes it intelligently, giving you back storage space
              and noticeably snappier performance in seconds.
            </p>
            <div className="flex flex-wrap gap-3">
              {["Performance", "Storage", "Optimization"].map((t) => (
                <span
                  key={t}
                  className="border border-gray-200 rounded-full px-4 py-2 text-xs text-gray-500 uppercase tracking-widest"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {beforeAfter.map((s) => (
            <div
              key={s.label}
              className="bg-white border border-gray-100 rounded-2xl p-6 text-center"
            >
              <div className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight mb-1">
                {s.value}
              </div>
              <div className="text-xs text-gray-400 uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (
            <div
              key={f.title}
              className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-cyan-400/10 rounded-xl mb-4">
                <i className={`${f.icon} text-cyan-500 text-lg w-5 h-5 flex items-center justify-center`} />
              </div>
              <h4 className="font-bold text-gray-900 text-base mb-2">{f.title}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
